import { DataRefreshEvent } from "@fp/admin-api-client";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import {
	PageContentContainer,
	PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { PageActionsMenu } from "@/components/page-actions-menu";
import { usePageActions } from "@/components/page-actions-context";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useDataRefresh } from "@/hooks/useSignalR";
import { useGetPayoutRequestStats, getGetPayoutRequestStatsQueryKey } from "@fp/admin-api-client/payout-requests/payout-requests.client";
import { useGetPayableStats, getGetPayableStatsQueryKey } from "@fp/admin-api-client/payouts/payouts.client";
import { ChevronRight, Send01 } from "@untitledui/icons";
import { cx } from "@fp/ui/utils/cx";
import { type PayCategory, PayCategoryLabels, type PayoutCategory, PayoutCategoryLabels } from "@/lib/pay-categories";
import { DirectPayoutModal } from "@/components/direct-payout-modal";
import { useGetOrganizationSettings } from "@fp/admin-api-client/organizations/organizations.client";
import { useSetPageActions } from "@/components/page-actions-context";

export const Route = createFileRoute("/payout-requests/")({
	component: RouteComponent,
	pendingComponent: () => <PageLoading />,
});

const payCategoryOrder: PayCategory[] = ["all", "review", "returned", "approved", "denied"];
const payoutCategoryOrder: PayoutCategory[] = ["all", "failed", "overdue", "scheduled", "released", "processing", "paid", "voided"];

type PayCategoryListItemProps = {
	category: PayCategory;
	count: number;
	onSelect: (category: PayCategory) => void;
};

const PayCategoryListItem = ({
	category,
	count,
	onSelect,
}: PayCategoryListItemProps) => {
	const isReviewCategory = category === "review" && count > 0;

	return (
		<button
			type="button"
			className={cx(
				"flex border border-primary rounded-lg px-4 py-3 justify-between",
				"text-left cursor-pointer hover:bg-gray-50",
				isReviewCategory && "bg-success-50 border-success-300",
			)}
			onClick={() => onSelect(category)}
		>
			<div className="font-semibold">
				<div className="flex items-center mb-1">
					<span className={cx(isReviewCategory && "text-success-600")}>
						{PayCategoryLabels[category]}
					</span>
				</div>
				{isReviewCategory && (
					<div className="text-sm font-normal text-gray-700">
						Payout requests need review
					</div>
				)}
			</div>
			<div className="flex items-center space-x-3 cursor-pointer">
				<div>{count}</div> <ChevronRight />
			</div>
		</button>
	);
};

type PayoutCategoryListItemProps = {
	category: PayoutCategory;
	count: number;
	onSelect: (category: PayoutCategory) => void;
};

const PayoutCategoryListItem = ({
	category,
	count,
	onSelect,
}: PayoutCategoryListItemProps) => {
	const isFailedCategory = category === "failed" && count > 0;
	const isOverdueCategory = category === "overdue" && count > 0;

	return (
		<button
			type="button"
			className={cx(
				"flex border border-primary rounded-lg px-4 py-3 justify-between",
				"text-left cursor-pointer hover:bg-gray-50",
				isFailedCategory && "bg-error-50 border-error-300",
				isOverdueCategory && "bg-warning-50 border-warning-300",
			)}
			onClick={() => onSelect(category)}
		>
			<div className="font-semibold">
				<div className="flex items-center mb-1">
					<span className={cx(isFailedCategory && "text-error-600", isOverdueCategory && "text-warning-600")}>
						{PayoutCategoryLabels[category]}
					</span>
				</div>
				{isFailedCategory && (
					<div className="text-sm font-normal text-gray-700">
						Payouts failed permanently
					</div>
				)}
				{isOverdueCategory && (
					<div className="text-sm font-normal text-gray-700">
						Payouts a disbursement run could not pay
					</div>
				)}
			</div>
			<div className="flex items-center space-x-3 cursor-pointer">
				<div>{count}</div> <ChevronRight />
			</div>
		</button>
	);
};

function RouteComponent() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { data: orgSettingsResponse } = useGetOrganizationSettings();
	const showDirectPayoutOnPay = orgSettingsResponse?.data?.showDirectPayoutOnPay ?? true;
	const [showPayModal, setShowPayModal] = useState(false);
	const pageActions = usePageActions();
	const hasPageActions = (pageActions?.actions.length ?? 0) > 0;

	useSetPageActions("Pay", useMemo(() => showDirectPayoutOnPay ? [
		{ id: "direct-payout", icon: Send01, label: "Direct Payout", onAction: () => setShowPayModal(true) },
	] : [], [showDirectPayoutOnPay]));

	const { data: requestData, isLoading: requestsLoading } = useGetPayoutRequestStats();
	const { data: payoutData, isLoading: payoutsLoading } = useGetPayableStats();

	const invalidatePayData = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: getGetPayoutRequestStatsQueryKey() });
		queryClient.invalidateQueries({ queryKey: getGetPayableStatsQueryKey() });
		queryClient.invalidateQueries({ queryKey: ["/api/payout-requests"], exact: false });
		queryClient.invalidateQueries({ queryKey: ["/api/payouts/payables"], exact: false });
	}, [queryClient]);

	useDataRefresh(DataRefreshEvent.PayoutRequestApproved, invalidatePayData);
	useDataRefresh(DataRefreshEvent.PayoutRequestDenied, invalidatePayData);
	useDataRefresh(DataRefreshEvent.PayoutRequestReturned, invalidatePayData);
	useDataRefresh(DataRefreshEvent.PayoutRequestSubmitted, invalidatePayData);
	useDataRefresh(DataRefreshEvent.PayoutRequestResubmitted, invalidatePayData);
	useDataRefresh(DataRefreshEvent.PayoutRequestEdited, invalidatePayData);
	useDataRefresh(DataRefreshEvent.PayoutRequestGenerated, invalidatePayData);
	useDataRefresh(DataRefreshEvent.PayoutRequestAllocated, invalidatePayData);
	useDataRefresh(DataRefreshEvent.PayoutRequestPaid, invalidatePayData);
	useDataRefresh(DataRefreshEvent.PayoutOverdue, invalidatePayData);
	useDataRefresh(DataRefreshEvent.PayoutTerminallyFailed, invalidatePayData);
	useDataRefresh(DataRefreshEvent.PayoutRetried, invalidatePayData);

	const requestStats = requestData?.data;
	const payoutStats = payoutData?.data;

	const isLoading = requestsLoading || payoutsLoading;

	const requestCounts: Record<PayCategory, number> = {
		all: requestStats?.all ?? 0,
		review: requestStats?.pending ?? 0,
		returned: requestStats?.returned ?? 0,
		approved: requestStats?.approved ?? 0,
		denied: requestStats?.denied ?? 0,
	};

	const payoutCounts: Record<PayoutCategory, number> = {
		all: payoutStats?.all ?? 0,
		scheduled: payoutStats?.scheduled ?? 0,
		released: payoutStats?.released ?? 0,
		processing: payoutStats?.processing ?? 0,
		paid: payoutStats?.paid ?? 0,
		failed: payoutStats?.failed ?? 0,
		overdue: payoutStats?.overdue ?? 0,
		voided: payoutStats?.voided ?? 0,
	};

	const handleCategorySelect = (category: PayCategory) => {
		navigate({
			to: "/payout-requests/categories/$category",
			params: { category },
		});
	};

	const handlePayoutCategorySelect = (category: PayoutCategory) => {
		navigate({
			to: "/payouts/categories/$category",
			params: { category },
		});
	};

	if (isLoading) {
		return (
			<AdminLayout>
				<AdminPageHeader title="Pay" />
				<PageContentContainer>
					<PageLoading />
				</PageContentContainer>
			</AdminLayout>
		);
	}

	return (
		<AdminLayout>
			<AdminPageHeader
				title="Pay"
				titleActions={hasPageActions ? <PageActionsMenu /> : undefined}
			/>
			<PageContentContainer className="p-0">
				<DirectPayoutModal isOpen={showPayModal} onClose={() => setShowPayModal(false)} />
				<section className="flex flex-col bg-white flex-grow">
					<div className="text-brand-600 p-5">
						<h2 className="text-lg font-semibold mb-3">Requests</h2>
						<div className="flex flex-col mb-8 space-y-3 text-md">
							{payCategoryOrder.map((category) => (
								<PayCategoryListItem
									key={category}
									category={category}
									count={requestCounts[category]}
									onSelect={handleCategorySelect}
								/>
							))}
						</div>

						<h2 className="text-lg font-semibold mb-3">Payouts</h2>
						<div className="flex flex-col mb-8 space-y-3 text-md">
							{payoutCategoryOrder.map((category) => (
								<PayoutCategoryListItem
									key={category}
									category={category}
									count={payoutCounts[category]}
									onSelect={handlePayoutCategorySelect}
								/>
							))}
						</div>
					</div>
				</section>
			</PageContentContainer>
		</AdminLayout>
	);
}